import { t } from '@lingui/macro'
import { useFormikContext } from 'formik'
import { useState } from 'react'
import ReactSelect from 'react-select'
import styled, { useTheme } from 'styled-components'

import { Checkbox } from '@/@bothrs-ui/web/components/forms/checkbox/Checkbox'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { getUnitOptions } from '../MedicationDosage.constants'

import type { DosageRow } from '../MedicationDosage.constants'
import type { MedicationFormValues } from '../MedicationQuestion.helpers'

// ================================ Component ================================

const DosageQuantityInput = ({ value, onChange }: { value: number; onChange: (value: number) => void }) => {
  const [text, setText] = useState(value ? String(value) : '')

  const handleChange = (event_: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event_.target.value
    const normalized = raw.replace(',', '.')

    if (raw !== '' && !/^\d*\.?\d*$/.test(normalized)) return

    setText(raw)

    const parsed = Number.parseFloat(normalized)
    if (!Number.isNaN(parsed)) {
      onChange(Math.min(999, Math.max(0, parsed)))
    } else if (raw === '') {
      onChange(0)
    }
  }

  const handleBlur = () => {
    setText(value ? String(value) : '')
  }

  return (
    <StQuantityInput
      type='text'
      inputMode='decimal'
      value={text}
      placeholder='0'
      onChange={handleChange}
      onBlur={handleBlur}
    />
  )
}

/**
 * Structured dosage input with time-of-day rows.
 *
 * Renders a row for each time slot (morning, midday, evening, before sleep)
 * where the patient enters a quantity (supports decimals) and selects a unit
 * (pills, ml, mg, drops, inhalations), plus an "indien nodig" checkbox
 * that sets `asNeededBoolean: true` on the FHIR dosage entries.
 *
 * Writes to the Formik fields `dosageRows` and `asNeededBoolean`.
 * Must be rendered inside a `<Formik>` context.
 */
export const MedicationDosageStructuredInput = () => {
  const theme = useTheme()
  const { values, setFieldValue } = useFormikContext<MedicationFormValues>()
  const rows = values.dosageRows
  const unitOptions = getUnitOptions()

  const handleRowChange = (index: number, update: Partial<DosageRow>) => {
    const newRows = rows.map((row, index_) =>
      index_ === index ? { ...row, ...update } : update.unit ? { ...row, unit: update.unit } : row,
    )
    setFieldValue('dosageRows', newRows)
  }

  return (
    <StRoot>
      <Span type='BodyLabel' color='#012249' style={{ textTransform: 'uppercase' }}>
        {t({
          id: 'medication.form.frequency.sectionLabel',
          message: 'Dagelijkse inname',
        })}
      </Span>
      <StRowsContainer>
        {rows.map((row, index) => (
          <StRow key={row.timeOfDay}>
            <StRowInputGroup>
              <DosageQuantityInput value={row.quantity} onChange={(quantity) => handleRowChange(index, { quantity })} />
              <StUnitWrapper>
                <ReactSelect
                  value={
                    unitOptions.find((o) => o.value === row.unit) ??
                    unitOptions.find((o) => o.value === 'pills') ??
                    null
                  }
                  options={unitOptions}
                  onChange={(option) =>
                    handleRowChange(index, {
                      unit: option?.value ?? row.unit,
                    })
                  }
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base, state) => ({
                      ...base,
                      minHeight: 'unset',
                      backgroundColor: theme.Components.Input.Fill,
                      border: `1px solid ${state.isFocused ? theme.Colors.Theme.Primary : theme.Colors.Neutrals.Light}`,
                      borderRadius: `${theme.UI.BorderRadius.Small}px`,
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? theme.Colors.Theme.Primary : theme.Colors.Neutrals.Light,
                      },
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '14px 16px',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: theme.Components.Input.Text,
                      fontSize: '16px',
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: theme.Colors.Theme.Primary,
                      padding: `0 ${theme.UI.SpacingPx.Space4} 0 0`,
                    }),
                  }}
                />
              </StUnitWrapper>
            </StRowInputGroup>
            <StRowLabel>{row.timeOfDay}</StRowLabel>
          </StRow>
        ))}
      </StRowsContainer>
      <StCheckboxRow
        $selected={values.asNeededBoolean}
        onClick={(event_) => {
          if ((event_.target as HTMLElement).closest('input, label')) return
          setFieldValue('asNeededBoolean', !values.asNeededBoolean)
        }}
      >
        <Checkbox
          id='asNeededBoolean'
          name='asNeededBoolean'
          type='checkbox'
          label={t({
            id: 'medication.frequency.ifNeeded',
            message: 'indien nodig',
          })}
          checked={values.asNeededBoolean}
          onChange={() => setFieldValue('asNeededBoolean', !values.asNeededBoolean)}
        />
      </StCheckboxRow>
    </StRoot>
  )
}

// ================================ Styled Components ================================

export const DOSAGE_INPUT_MIN_HEIGHT = 340

const StRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  flex-grow: 1;
  min-height: ${DOSAGE_INPUT_MIN_HEIGHT}px;
`

const StRowsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
`

const StRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
  width: 100%;
`

const StRowInputGroup = styled.div`
  display: flex;
  flex: 3;
  min-width: 0;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StRowLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`

const StQuantityInput = styled.input`
  width: 64px;
  flex-shrink: 0;
  padding: 14px ${({ theme }) => theme.UI.SpacingPx.Space4};
  border: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  font-size: 16px;
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
  text-align: center;
  box-sizing: border-box;
  outline: none;

  /* Hide browser number input spinners */
  -moz-appearance: textfield;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  }

  &:focus {
    border-color: ${({ theme }) => theme.Colors.Theme.Primary};
  }
`

const StCheckboxRow = styled.div<{ $selected: boolean }>`
  padding: 14px ${({ theme }) => theme.UI.SpacingPx.Space4};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.Components.Conversational.ActiveBorder : theme.Components.Conversational.Border};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.Components.Conversational.ActiveFill : theme.Components.Conversational.Fill};
  cursor: pointer;

  > div {
    align-items: center;

    > div:first-child {
      margin-top: 0;
    }

    > label {
      line-height: 1;
    }
  }
`

const StUnitWrapper = styled.div`
  flex: 1;
  min-width: 0;
`
